import classnames from "classnames";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";

import { FormInfoProvider } from "@utils/forms/formInfo.provider";
import * as ValidationRules from "@utils/validations";
import * as PhoneValidationRules from "@utils/validations/phone";

import { trackContactStepStarted } from "@services/tracking";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { ContactCustomerServiceForm } from "@components/ContactCustomerservice/contactCustomerservice.types";
import Rules, {
    AdditionalFAQField,
} from "@components/ContactCustomerservice/helpers/rules.helpers";
import { FormFieldTouchedCallback } from "@components/Form/Callbacks/formFieldCallbacks.component";
import { PhoneField } from "@components/Form/FormFields/PhoneField/phoneField.container";
import SelectField from "@components/Form/FormFields/SelectField/selectField.component";
import { StoreField } from "@components/Form/FormFields/StoreField/storeField.container";
import { TextAreaField } from "@components/Form/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@components/Form/FormFields/TextField/textField.component";
import { Button } from "@components/General/Button/button.component";
import { Col } from "@components/General/Grid/col.component";
import { Container } from "@components/General/Grid/container.component";
import { Row } from "@components/General/Grid/row.component";
import { Messages } from "@components/General/Messages/messages.container";

type Props = {
    onSubmit: (
        formValues: ContactCustomerServiceForm,
        { resetForm }: FormikHelpers<ContactCustomerServiceForm>,
    ) => Promise<void>;
    isSubmitting: boolean;
    categoriesFormOptions: Array<{ label: string; value: string }>;
    initialFormValues: ContactCustomerServiceForm;
    loading: boolean;
};

const FORM_ID = "contactCustomerserviceForm";

const ContactCustomerservice = ({
    onSubmit,
    isSubmitting,
    categoriesFormOptions,
    initialFormValues,
    loading,
}: Props) => {
    const { t } = useGranite();

    const onFormTouched = () => {
        trackContactStepStarted();
    };

    return (
        <>
            <Messages messageid={"contact-customerservice-request-success"} />
            <Messages messageid={"contact-customerservice-request-failure"} />
            <FormInfoProvider formId={FORM_ID} withNeverbounce={true}>
                {({ initialized }) => (
                    <Formik
                        onSubmit={onSubmit}
                        initialValues={initialFormValues}
                        enableReinitialize={true}
                    >
                        {({ values, touched }) => {
                            const additionalFAQField = Rules.parseValue(
                                values.category,
                            );

                            return (
                                <Form id={FORM_ID}>
                                    <FormFieldTouchedCallback
                                        touched={touched}
                                        callback={onFormTouched}
                                    />
                                    <Container className={classnames("px-0")}>
                                        <Row>
                                            <Col md={6}>
                                                <SelectField
                                                    name="category"
                                                    label={t(
                                                        "general.form.category.label",
                                                    )}
                                                    placeholder={t(
                                                        "general.form.category.placeholder",
                                                    )}
                                                    autoComplete={"off"}
                                                    required={true}
                                                    optionList={
                                                        categoriesFormOptions
                                                    }
                                                    validationRule={
                                                        ValidationRules.categories
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        {additionalFAQField ===
                                            AdditionalFAQField.OrderRegistryNumber && (
                                            <Row>
                                                <Col>
                                                    <TextField
                                                        name="orderId"
                                                        label={t(
                                                            "general.form.orderNumber.label",
                                                        )}
                                                        required={false}
                                                        placeholder={t(
                                                            "general.form.orderNumber.placeholder",
                                                        )}
                                                        disabled={
                                                            loading ||
                                                            !initialized
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        <Row>
                                            <Col md={6}>
                                                <TextField
                                                    name="firstName"
                                                    label={t(
                                                        "general.form.firstName.label",
                                                    )}
                                                    required={false}
                                                    autoComplete={"given-name"}
                                                    placeholder={t(
                                                        "general.form.firstName.placeholder",
                                                    )}
                                                    validationRule={
                                                        ValidationRules.optionalFirstName
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <TextField
                                                    name="lastName"
                                                    label={t(
                                                        "general.form.lastName.label",
                                                    )}
                                                    required
                                                    autoComplete={"family-name"}
                                                    placeholder={t(
                                                        "general.form.lastName.placeholder",
                                                    )}
                                                    validationRule={
                                                        ValidationRules.lastName
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={6}>
                                                <TextField
                                                    name="email"
                                                    label={t(
                                                        "general.form.email.label",
                                                    )}
                                                    required
                                                    autoComplete={"email"}
                                                    validationRule={
                                                        ValidationRules.emailWithNB
                                                    }
                                                    placeholder={t(
                                                        "general.form.email.placeholder",
                                                    )}
                                                    debounce={true}
                                                    parseToString={true}
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <PhoneField
                                                    name="phone"
                                                    autoComplete={
                                                        "tel-national"
                                                    }
                                                    label={t(
                                                        "general.form.phoneNumber.label",
                                                    )}
                                                    placeholder={t(
                                                        "general.form.phoneNumber.placeholder",
                                                    )}
                                                    required={false}
                                                    validationRule={
                                                        PhoneValidationRules.optionalPhone
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={6}>
                                                <TextField
                                                    type={"number"}
                                                    name="bonusCardNumber"
                                                    label={t(
                                                        "general.form.bonusCard.label",
                                                    )}
                                                    placeholder={t(
                                                        "general.form.bonusCard.placeholder",
                                                    )}
                                                    // @ts-ignore
                                                    min="0"
                                                    required={false}
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    validationRule={
                                                        ValidationRules.optionalBonusCard
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <StoreField
                                                    name="storeId"
                                                    label={t(
                                                        "general.form.store.label",
                                                    )}
                                                    placeholder={t(
                                                        "general.form.store.placeholder",
                                                    )}
                                                    required
                                                    validationRule={
                                                        ValidationRules.storeId
                                                    }
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <TextAreaField
                                                    name="message"
                                                    label={t(
                                                        "general.form.message.label",
                                                    )}
                                                    required
                                                    placeholder={t(
                                                        "general.form.message.placeholder",
                                                    )}
                                                    validationRule={ValidationRules.message(
                                                        1500,
                                                    )}
                                                    maxLength={1500}
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <Button
                                                    test="contactCustomerService-button-submit"
                                                    type={"submit"}
                                                    loading={isSubmitting}
                                                    disabled={
                                                        loading || !initialized
                                                    }
                                                >
                                                    {t("general.form.send")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Container>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
            </FormInfoProvider>
        </>
    );
};

export default ContactCustomerservice;
